import React, { useEffect, useState } from "react";

import { JSX } from "react/jsx-runtime";
import {
  Box,
  CircularProgress,
  MenuItem,
  Stack,
  TablePagination,
} from "@mui/material";

import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { brown, pink } from "@mui/material/colors";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import ButtonBringOffline from "./buttons/ButtonBringOffline";
import ButtonBringOnline from "./buttons/ButtonBringOnline";
import ButtonEditInfo from "./buttons/ButtonEditInfo";
import ButtonSetContractGate from "./buttons/ButtonSetContractGate";
import ButtonDappLink from "./buttons/ButtonDappLink";
import { EveButton } from "@eveworld/ui-components";

async function getSmartAssemblies(smartCharacterAddress: string) {
  try {
    const res = await fetch(
      `https://blockchain-gateway-stillness.live.tech.evefrontier.com/smartcharacters/${smartCharacterAddress}`
    );

    const data = await res.json();

    return data.smartAssemblies;
  } catch (error) {
    console.log(error);
  }
}

async function getSmartAssembly(smartAssemblyId: string) {
  try {
    const res = await fetch(
      `https://blockchain-gateway-stillness.live.tech.evefrontier.com/smartassemblies/${smartAssemblyId}`
    );

    const data = await res.json();
    //console.log(data);
    return data;
  } catch (error) {
    console.log(error);
  }
}

async function getSmartAssemblyConfig(smartAssemblyId: string) {
  try {
    const requestData: QueryRequest[] = [
      {
        address: "0x90373cf89e73168cdf90e99d0a7fa9c4b5625c6a",
        query:
          'SELECT "systemId" FROM "eveworld__SmartGateConfigT" WHERE smartObjectId=' +
          smartAssemblyId.toString(),
      },
    ];

    return await fetchTableData(requestData).then((data) => data);
  } catch (error) {
    console.log(error);
  }
}

interface QueryRequest {
  address: string;
  query: string;
}

const fetchTableData = async (
  requestData: QueryRequest[],
  apiUrl: string = "https://indexer.mud.pyropechain.com/q"
): Promise<any> => {
  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching table data:", error);
    return null;
  }
};

class AssemblyData {
  id!: string;
  state!: string;
  name!: string;
  assemblyType!: string;
  isOnline!: boolean;
  solarSystem!: string;
  solarSystemName!: string;
  solarSystemId!: string;
  dappUrl!: string;
  description!: string;
  fuel!: string;
  fuelAmount!: string;
  systemAddress!: string;

  constructor(data: AssemblyData) {
    Object.assign(this, data);
  }
}

function rowData(
  id: string,
  state: string,
  name: string,
  assemblyType: string,
  isOnline: boolean,
  solarSystemName: string,
  solarSystemId: string,
  dappUrl: string,
  description: string,
  fuelAmount: string,
  systemAddress: string
) {
  return {
    id,
    state,
    name,
    assemblyType,
    isOnline,
    solarSystemName,
    solarSystemId,
    dappUrl,
    description,
    fuelAmount,
    systemAddress,
  };
}

function Row(props: {
  row: ReturnType<typeof rowData>;
  handleRefreshGate: (data: ReturnType<typeof rowData>) => Promise<void>;
}) {
  const { row, handleRefreshGate } = props;
  const [open, setOpen] = React.useState(false);

  function handleRefreshGateClicked(row: ReturnType<typeof rowData>): void {
    handleRefreshGate(row);
  }

  return (
    <React.Fragment>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell
          component="th"
          scope="row"
          align="left"
          sx={{ fontSize: "13px" }}
        >
          {row.solarSystemName}
        </TableCell>
        <TableCell align="left" sx={{ fontSize: "13px" }}>
          {row.state}
        </TableCell>
        <TableCell align="left" sx={{ fontSize: "13px" }}>
          {row.name}
        </TableCell>
        <TableCell align="left" sx={{ fontSize: "13px" }}>
          {row.isOnline.toString()}
        </TableCell>
        <TableCell align="right" sx={{ fontSize: "13px" }}>
          {row.fuelAmount}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                margin: 0,
                padding: 1,
                backgroundColor: brown[200],
                color: pink[900],
              }}
            >
              <Typography variant="subtitle2" gutterBottom component="div">
                Further Details for {row.name}
              </Typography>
              <Typography variant="subtitle2" gutterBottom component="div">
                Smart Assembly Id: {row.id}
              </Typography>
              <Typography variant="subtitle2" gutterBottom component="div">
                Fuel Remaining: {row.fuelAmount}
              </Typography>
              <Typography variant="subtitle2" gutterBottom component="div">
                System Contract Address: {row.systemAddress}
              </Typography>
              <Stack direction={"row"} spacing={1}>
                <ButtonBringOffline AssemblyId={row.id}></ButtonBringOffline>
                <ButtonBringOnline AssemblyId={row.id}></ButtonBringOnline>
                <ButtonSetContractGate
                  AssemblyId={row.id}
                ></ButtonSetContractGate>
                <ButtonDappLink AssemblyId={row.id}></ButtonDappLink>
                {/* <ButtonRefreshGate AssemblyId={row.id}></ButtonRefreshGate> */}
                <EveButton
                  typeClass="primary"
                  onClick={() => handleRefreshGateClicked(row)}
                >
                  Refresh
                </EveButton>
              </Stack>
              <br></br>
              <Stack direction={"row"} spacing={1}>
                <ButtonEditInfo
                  AssemblyId={row.id}
                  NewName={row.name}
                  NewDappUrl={row.dappUrl}
                  NewDescription={row.description}
                ></ButtonEditInfo>
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

const BuilderSmartGates = ({
  smartCharacterAddress,
}: {
  smartCharacterAddress: string;
}): JSX.Element => {
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filterSolarSystemName, setFilterSolarSystemName] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterOnline, setFilterOnline] = useState("");
  const [assemblyDatas, setAssemblyDatas] = useState<AssemblyData[] | null>(
    null
  );

  const handleRefreshGate = async (data: ReturnType<typeof rowData>) => {
    await getSmartAssembly(data.id).then(async (r) => {
      data.dappUrl = r.dappUrl;
      data.name = r.name;
      data.description = r.description;
      data.state = r.state;
      data.fuelAmount = "0";

      if (r.fuel && r.fuel.fuelAmount.length > 0) {
        data.fuelAmount = (
          BigInt(r.fuel.fuelAmount) / 1000000000000000000n
        ).toString();
      }

      await getSmartAssemblyConfig(r.id).then((r2) => {
        const [subArray] = r2.result;
        data.systemAddress = subArray[1].toString();
      });

      if (assemblyDatas != null) {
        setAssemblyDatas(
          assemblyDatas.map(
            (item) => (item.id === data.id ? { ...item, id: data.id } : item) //don't need to change anything, just using this to update state
          )
        );
      }

      console.log(data.description);
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleChangeFilterSolarSystemName = (event: SelectChangeEvent) => {
    setFilterSolarSystemName(event.target.value as string);
  };
  const handleChangeFilterState = (event: SelectChangeEvent) => {
    setFilterState(event.target.value as string);
  };
  const handleChangeFilterName = (event: SelectChangeEvent) => {
    setFilterName(event.target.value as string);
  };
  const handleChangeFilterOnline = (event: SelectChangeEvent) => {
    setFilterOnline(event.target.value as string);
  };

  //let assemblyDataList: AssemblyData[] = [];
  let assemblyDatasFiltered: AssemblyData[] = [];
  let solarSystemNameList: JSX.Element[] = [];
  let nameList: JSX.Element[] = [];

  useEffect(() => {
    let active = true;

    load();
    return () => {
      active = false;
    };

    async function load() {
      await getSmartAssemblies(smartCharacterAddress).then((r) => {
        const json = r.filter(
          (data: { assemblyType: string }) => data.assemblyType == "SmartGate"
        );

        var tempAssemblies: AssemblyData[] = JSON.parse(JSON.stringify(json));

        //extract the solar system name etc. from the nested JSON
        if (tempAssemblies != null) {
          const assembliesComplete = tempAssemblies.map(async (c, i) => {
            c.solarSystemName = JSON.parse(
              JSON.stringify(c.solarSystem)
            ).solarSystemName;
            c.solarSystemId = JSON.parse(
              JSON.stringify(c.solarSystem)
            ).solarSystemId;

            await getSmartAssembly(c.id).then((r) => {
              c.dappUrl = r.dappUrl;
              c.description = r.description;

              c.fuelAmount = "0";

              if (r.fuel && r.fuel.fuelAmount.length > 0) {
                c.fuelAmount = (
                  BigInt(r.fuel.fuelAmount) / 1000000000000000000n
                ).toString();
              }
            });

            await getSmartAssemblyConfig(c.id).then((r) => {
              const [subArray] = r.result;
              if (subArray.size > 1) {
                c.systemAddress = subArray[1].toString();
              }
            });

            return c;
          });

          //exclude blank solar system names, these are borked or non-present gates i.e. removed
          Promise.all(assembliesComplete).then((r) => {
            setAssemblyDatas(
              r.filter(
                (data: { solarSystemName: string }) => data.solarSystemName > ""
              )
            );
          });
        }
      });

      if (!active) {
        return;
      }

      setLoading(false);
    }
  }, [smartCharacterAddress]);

  if (loading) {
    return <CircularProgress />;
  } else {
    //***FILTER VALUE LISTS***

    let assemblyDatasForSolarSystemNameFilter = assemblyDatas;
    assemblyDatasForSolarSystemNameFilter?.sort((a, b) => {
      if (a.solarSystemName < b.solarSystemName) {
        return -1;
      }
      if (a.solarSystemName > b.solarSystemName) {
        return 1;
      }
      return 0;
    });

    solarSystemNameList.push(
      <MenuItem value={""} key={"blank"} sx={{ fontSize: 12 }}>
        {""}
      </MenuItem>
    );

    assemblyDatasForSolarSystemNameFilter?.forEach((data) => {
      solarSystemNameList.push(
        <MenuItem
          value={data.solarSystemName}
          key={data.id}
          sx={{ fontSize: 12 }}
        >
          {data.solarSystemName}
        </MenuItem>
      );
    });

    let assemblyDatasForNameFilter = assemblyDatas;
    assemblyDatasForNameFilter?.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });

    nameList.push(
      <MenuItem value={""} key={"blank"} sx={{ fontSize: 12 }}>
        {""}
      </MenuItem>
    );

    assemblyDatasForNameFilter?.forEach((data) => {
      if (data.name.length > 0) {
        nameList.push(
          <MenuItem value={data.name} key={data.id} sx={{ fontSize: 12 }}>
            {data.name}
          </MenuItem>
        );
      }
    });

    //***APPLY FILTERS***

    let assemblyDatasFiltered = assemblyDatas;

    if (assemblyDatasFiltered != null) {
      if (filterSolarSystemName.length > 0) {
        assemblyDatasFiltered = assemblyDatasFiltered.filter(
          (data) => data.solarSystemName == filterSolarSystemName
        );
      }
      if (filterState.length > 0) {
        assemblyDatasFiltered = assemblyDatasFiltered?.filter(
          (data) => data.state == filterState
        );
      }
      if (filterName.length > 0) {
        assemblyDatasFiltered = assemblyDatasFiltered?.filter(
          (data) => data.name == filterName
        );
      }
      if (filterOnline.length > 0) {
        assemblyDatasFiltered = assemblyDatasFiltered?.filter(
          (data) => data.isOnline.toString() == filterOnline
        );
      }
    }

    function handleMapClicked(): void {
      if (assemblyDatasFiltered == null) {
        return;
      }

      const uniqueSystemIds = [
        ...new Set(assemblyDatasFiltered.map((item) => item.solarSystemId)),
      ];
      //console.log(uniqueSystemIds);

      window.open(
        "https://frontier-atlas.com/map?pins=" + uniqueSystemIds.join(","),
        "_blank"
      );
    }

    return (
      <>
        <p>
          Below are a list of your personal Smart Gates. Expand the row to
          perform actions such as Offline / Online
        </p>
        <div className="gates-container">
          <TableContainer component={Paper}>
            <Table stickyHeader size="small" aria-label="collapsible table">
              {/* header row */}
              <TableHead
                sx={{
                  "& .MuiTableCell-head": {
                    backgroundColor: brown[300],
                    color: pink[900],
                  },
                }}
              >
                <TableRow>
                  <TableCell align="left">
                    <EveButton
                      typeClass="primary"
                      onClick={() => handleMapClicked()}
                    >
                      Map
                    </EveButton>
                  </TableCell>
                  <TableCell align="left" style={{ minWidth: "200px" }}>
                    Solar System
                  </TableCell>
                  <TableCell align="left" style={{ minWidth: "200px" }}>
                    State
                  </TableCell>
                  <TableCell align="left" style={{ minWidth: "200px" }}>
                    Name
                  </TableCell>
                  <TableCell align="left" style={{ minWidth: "90px" }}>
                    Online?
                  </TableCell>
                  <TableCell align="left" style={{ minWidth: "90px" }}>
                    Fuel
                  </TableCell>
                </TableRow>
              </TableHead>
              {/* filter row */}
              <TableHead
                sx={{
                  "& .MuiTableCell-head": {
                    backgroundColor: brown[200],
                    color: pink[900],
                  },
                }}
              >
                <TableRow>
                  <TableCell>Filters:</TableCell>
                  <TableCell align="left" style={{ minWidth: "200px" }}>
                    <Select
                      id="filter-solarSystemName"
                      value={filterSolarSystemName}
                      label="Solar System"
                      onChange={handleChangeFilterSolarSystemName}
                      style={{
                        minWidth: "100%",
                        color: "black",
                        fontFamily: "Roboto",
                        fontSize: 12,
                      }}
                    >
                      {solarSystemNameList}
                    </Select>
                  </TableCell>
                  <TableCell align="left" style={{ minWidth: "200px" }}>
                    <Select
                      id="filter-state"
                      value={filterState}
                      label="State"
                      onChange={handleChangeFilterState}
                      style={{
                        minWidth: "100%",
                        color: "black",
                        fontFamily: "Roboto",
                        fontSize: 12,
                      }}
                    >
                      <MenuItem value={""} sx={{ fontSize: 12 }}></MenuItem>
                      {/* <MenuItem value={"Anchoring"} sx={{ fontSize: 12 }}>Anchoring</MenuItem> */}
                      <MenuItem value={"Anchored"} sx={{ fontSize: 12 }}>
                        Anchored
                      </MenuItem>
                      <MenuItem value={"Online"} sx={{ fontSize: 12 }}>
                        Online
                      </MenuItem>
                      <MenuItem value={"Unanchored"} sx={{ fontSize: 12 }}>
                        Unanchored
                      </MenuItem>
                      <MenuItem value={"Destroyed"} sx={{ fontSize: 12 }}>
                        Destroyed
                      </MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell align="left" style={{ minWidth: "200px" }}>
                    <Select
                      id="filter-name"
                      value={filterName}
                      label="Id"
                      onChange={handleChangeFilterName}
                      style={{
                        minWidth: "100%",
                        color: "black",
                        fontFamily: "Roboto",
                        fontSize: 12,
                      }}
                    >
                      {nameList}
                    </Select>
                  </TableCell>
                  <TableCell align="left" style={{ minWidth: "90px" }}>
                    <Select
                      id="filter-online"
                      value={filterOnline}
                      label="Online"
                      onChange={handleChangeFilterOnline}
                      style={{
                        minWidth: "100%",
                        color: "black",
                        fontFamily: "Roboto",
                        fontSize: 12,
                      }}
                    >
                      <MenuItem value={""} sx={{ fontSize: 12 }}></MenuItem>
                      <MenuItem value={"true"} sx={{ fontSize: 12 }}>
                        true
                      </MenuItem>
                      <MenuItem value={"false"} sx={{ fontSize: 12 }}>
                        false
                      </MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody
                sx={{
                  "& .MuiTableCell-root": {
                    color: pink[900],
                    fontWeight: 500,
                  },
                }}
              >
                {assemblyDatasFiltered &&
                  assemblyDatasFiltered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((data: any) => (
                      <Row
                        key={data.id}
                        row={data}
                        handleRefreshGate={handleRefreshGate}
                      />
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Paper>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={assemblyDatasFiltered?.length ?? 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </div>
      </>
    );
  }
};

export default React.memo(BuilderSmartGates);
